import { create } from 'zustand'
import { persist, createJSONStorage, devtools } from 'zustand/middleware'
import { useEffect, useState } from 'react'

const AUTH_STORAGE_KEY = 'auth-storage'
const AUTH_SYNC_REQUEST = 'auth-storage-request'
const AUTH_SYNC_RESPONSE = 'auth-storage-response'
const AUTH_LOGOUT_SYNC = 'auth-storage-logout'

function safeStorageRemove(storage: Storage, key: string) {
  try {
    storage.removeItem(key)
  } catch {
    // ignore quota / private-mode errors
  }
}

/** Copy the login access token to a new tab without keeping it after the browser/app restarts. */
function shareAccessTokenAcrossTabs(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  // Undo the localStorage persist mistake so a restart is not still logged in.
  safeStorageRemove(localStorage, AUTH_STORAGE_KEY)

  try {
    if (sessionStorage.getItem(AUTH_STORAGE_KEY)) {
      return Promise.resolve()
    }
  } catch {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timeout)
      window.removeEventListener('storage', onStorage)
      safeStorageRemove(localStorage, AUTH_SYNC_REQUEST)
      safeStorageRemove(localStorage, AUTH_SYNC_RESPONSE)
      resolve()
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_SYNC_RESPONSE || !event.newValue) return
      try {
        sessionStorage.setItem(AUTH_STORAGE_KEY, event.newValue)
      } catch {
        // ignore
      }
      finish()
    }

    const timeout = window.setTimeout(finish, 2000)
    window.addEventListener('storage', onStorage)
    try {
      localStorage.setItem(AUTH_SYNC_REQUEST, String(Date.now()))
    } catch {
      finish()
    }
  })
}

function listenForAccessTokenShare() {
  if (typeof window === 'undefined') return

  window.addEventListener('storage', (event) => {
    if (event.key === AUTH_SYNC_REQUEST && event.newValue) {
      try {
        const current = sessionStorage.getItem(AUTH_STORAGE_KEY)
        if (current) {
          localStorage.setItem(AUTH_SYNC_RESPONSE, current)
          window.setTimeout(() => safeStorageRemove(localStorage, AUTH_SYNC_RESPONSE), 500)
        }
      } catch {
        // ignore
      }
    }

    if (event.key === AUTH_LOGOUT_SYNC && event.newValue) {
      safeStorageRemove(sessionStorage, AUTH_STORAGE_KEY)
      useAuthStore.setState({
        token: null,
        tokenExpires: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        needsBranchSelection: false,
        availableBranches: [],
      })
    }
  })
}

// Define user roles for RBAC (can be extended based on branchUserRoleId)
export type UserRole = 'Admin' | 'Finance' | 'HR' | 'Employee' | 'Customer'

export interface Branch {
  branchId: number
  branchName: string
  branchUserRoleId: number
  currencyId?: number
  currency?: string
}

export interface User {
  userId: number
  userName: string
  email: string
  mobile: string
  isActive: boolean
  selectedBranch?: Branch
  branches: Branch[]
}

export interface AuthState {
  // Authentication state
  token: string | null
  tokenExpires: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Branch selection state
  needsBranchSelection: boolean
  availableBranches: Branch[]

  // Actions
  login: (token: string, expires: string, userData: Omit<User, 'selectedBranch'>) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  updateUser: (user: Partial<User>) => void
  selectBranch: (branch: Branch) => void
  switchBranch: () => void  // New function to switch branch
  setNeedsBranchSelection: (needs: boolean) => void
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
  isTokenExpired: () => boolean
}

/**
 * Authentication store using Zustand with persistence
 * Manages user authentication state, JWT tokens, and branch selection
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        token: null,
        tokenExpires: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        needsBranchSelection: false,
        availableBranches: [],

        // Login action - sets tokens and user data
        login: (token: string, expires: string, userData: Omit<User, 'selectedBranch'>) => {
          set(
            {
              token,
              tokenExpires: expires,
              user: userData,
              isAuthenticated: false, // Will be true only after branch selection
              isLoading: false,
              needsBranchSelection: true,
              availableBranches: userData.branches,
            },
            false,
            'auth/login'
          )
        },

        // Logout action - clears all auth data
        logout: () => {
          set(
            {
              token: null,
              tokenExpires: null,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              needsBranchSelection: false,
              availableBranches: [],
            },
            false,
            'auth/logout'
          )
          try {
            localStorage.setItem(AUTH_LOGOUT_SYNC, String(Date.now()))
            localStorage.removeItem(AUTH_LOGOUT_SYNC)
          } catch {
            // ignore
          }
        },

        // Set loading state for auth operations
        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, 'auth/setLoading')
        },

        // Update user information
        updateUser: (userUpdate: Partial<User>) => {
          const currentUser = get().user
          if (currentUser) {
            set(
              { user: { ...currentUser, ...userUpdate } },
              false,
              'auth/updateUser'
            )
          }
        },

        // Select branch and complete authentication
        selectBranch: (branch: Branch) => {
          const currentUser = get().user
          if (currentUser) {
            set(
              {
                user: { ...currentUser, selectedBranch: branch },
                isAuthenticated: true,
                needsBranchSelection: false,
              },
              false,
              'auth/selectBranch'
            )
          }
        },

        // Switch branch - go back to branch selection
        switchBranch: () => {
          const currentUser = get().user
          if (currentUser) {
            set(
              {
                user: { ...currentUser, selectedBranch: undefined },
                isAuthenticated: false,
                needsBranchSelection: true,
                availableBranches: currentUser.branches,
              },
              false,
              'auth/switchBranch'
            )
          }
        },

        // Set branch selection requirement
        setNeedsBranchSelection: (needs: boolean) => {
          set({ needsBranchSelection: needs }, false, 'auth/setNeedsBranchSelection')
        },

        // Check if user has specific role (based on branchUserRoleId)
        hasRole: (role: UserRole) => {
          const user = get().user
          if (!user?.selectedBranch) return false

          const roleMap: Record<number, UserRole[]> = {
            1: ['Admin'],
            2: ['Finance'],
            3: ['HR'],
            4: ['Employee'],
            5: ['Customer'],
          }

          const id = Number(user.selectedBranch.branchUserRoleId)
          const userRoles =
            Number.isFinite(id) && roleMap[id]?.length ? roleMap[id]! : ['Admin']
          return userRoles.includes(role)
        },

        // Check if user has any of the specified roles
        hasAnyRole: (roles: UserRole[]) => {
          return roles.some(role => get().hasRole(role))
        },

        // Check if token is expired
        isTokenExpired: () => {
          const { tokenExpires } = get()
          if (!tokenExpires) return true
          return new Date() >= new Date(tokenExpires)
        },
      }),
      {
        name: AUTH_STORAGE_KEY,
        // Access token from login lives in sessionStorage (cleared on app/browser restart).
        // New tabs copy it from an already-open tab; it is not kept in localStorage.
        storage: createJSONStorage(() => sessionStorage),
        skipHydration: true,
        partialize: (state) => ({
          token: state.token,
          tokenExpires: state.tokenExpires,
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          needsBranchSelection: state.needsBranchSelection,
          availableBranches: state.availableBranches,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
)

if (typeof window !== 'undefined') {
  listenForAccessTokenShare()
  void (async () => {
    await shareAccessTokenAcrossTabs()
    await useAuthStore.persist.rehydrate()
    const { token, isTokenExpired, logout } = useAuthStore.getState()
    if (token && isTokenExpired()) {
      logout()
    }
  })()
}

/** Wait until persisted login is loaded. Avoids sending a new tab to /login before hydrate. */
export function useAuthHydrated(): boolean {
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated())

  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true)
      return
    }
    return useAuthStore.persist.onFinishHydration(() => setHydrated(true))
  }, [])

  return hydrated
} 